import React, { useEffect, useRef, useState } from "react";
import { type MailTemplate, mailTemplateService } from "../../services/mailTemplateService";
import { SandboxedMailBody } from "./SandboxedMailBody";
import { RichTextEditor, RichTextEditorHandle } from "./richtext/RichTextEditor";
import {
    sanitizeComposeHtml,
    SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES,
    SIGNATURE_HTML_MAX_CHARS,
    SIGNATURE_IMAGE_MAX_BYTES,
} from "./composeSanitize";
import { embeddedImageBytes } from "./signatureSectionLogic";
import { ConfirmDialog } from "../redesign/ConfirmDialog";
import { Toast } from "../redesign/Toast";
import { MiniSpinner } from "../chat/chatUi";
import { I } from "../redesign/kvpIcons";
import "./mailSettings.css";
import { reportMailError } from "../../services/mailError";

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({
    label,
    children,
    hint,
}) => (
    <div>
        <label className="mset-label">{label}</label>
        {children}
        {hint && <div className="mset-hint">{hint}</div>}
    </div>
);

/**
 * CRUD-Section für Mail-Vorlagen: Liste, Anlege-Formular, Inline-Bearbeitung
 * und Löschen.
 *
 * Der Inhalt wird im WYSIWYG-Editor gepflegt (vorher: HTML-Quelltext in einer
 * Monospace-Textarea). Bilder landen als `data:`-URI im gespeicherten HTML und
 * werden erst beim Senden zu Inline-Anhängen — siehe ADR 0003.
 */
export const MailTemplateSection: React.FC = () => {
    const [rows, setRows] = useState<MailTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<number | null>(null);

    // Anlege-/Bearbeitungsformular. editingId === null → Neuanlage.
    const [editingId, setEditingId] = useState<number | null>(null);
    const [name, setName] = useState("");
    const [subject, setSubject] = useState("");
    const [initialBody, setInitialBody] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Der Editor ist uncontrolled; sein Inhalt wird ausschließlich über den
    // Handle gelesen — nie über einen parallel geführten State.
    const editorRef = useRef<RichTextEditorHandle>(null);

    // Vorschau bewusst NICHT an jeden Tastendruck gekoppelt: Das iframe würde
    // sonst bei jedem Zeichen neu laden, mit potenziell hunderten KB base64.
    const [preview, setPreview] = useState("");

    const [toast, setToast] = useState<{ msg: string; tone: "error" | "success" } | null>(null);
    const [confirmRow, setConfirmRow] = useState<MailTemplate | null>(null);

    useEffect(() => {
        void loadAll();
    }, []);

    const loadAll = async () => {
        setLoading(true);
        setError(null);
        try {
            setRows(await mailTemplateService.list());
        } catch (error) {
            setError(reportMailError({
                area: "mail",
                action: "load-settings",
                error,
                fallbackMessage: "Vorlagen konnten nicht geladen werden.",
            }));
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setName("");
        setSubject("");
        setInitialBody("");
        setPreview("");
    };

    const startEdit = (row: MailTemplate) => {
        setEditingId(row.id);
        setName(row.name);
        setSubject(row.subject);
        setInitialBody(row.bodyHtml);
        setPreview(row.bodyHtml);
    };

    const refreshPreview = () => setPreview(editorRef.current?.getHtml() ?? "");

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSubmitting(true);
        try {
            // Immer über den Handle lesen — so ist auch der letzte Tastenanschlag
            // vor dem Klick auf Speichern enthalten.
            const bodyHtml = sanitizeComposeHtml(editorRef.current?.getHtml() ?? "");
            // Dieselben Vorabprüfungen wie bei den Signaturen (die Vorlagen
            // teilen sich deren Grenzen und DTO-Längen). Ohne sie lief ein zu
            // großes Bild in den rohen 400 des Servers — technisch korrekt, aber
            // der Text sagt niemandem, was zu tun ist.
            const bilderBytes = embeddedImageBytes(bodyHtml);
            if (bilderBytes > SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES) {
                setToast({
                    msg: `Die eingebetteten Bilder sind zusammen zu groß (${Math.round(bilderBytes / 1024)} KB, erlaubt sind ${Math.round(SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES / 1024)} KB). Bitte kleinere Bilder verwenden.`,
                    tone: "error",
                });
                setSubmitting(false);
                return;
            }
            if (bodyHtml.length > SIGNATURE_HTML_MAX_CHARS) {
                setToast({
                    msg: "Die Vorlage ist insgesamt zu groß. Bitte Bilder verkleinern.",
                    tone: "error",
                });
                setSubmitting(false);
                return;
            }
            // `scope` wird bewusst NICHT mitgesendet. Neue Vorlagen bekommen den
            // Service-Default 'user'; bei bestehenden bleibt der gespeicherte Wert
            // unangetastet, weil das Update ein echtes Teil-Update ist
            // (mail-template.service.ts: `if (patch.scope !== undefined …)`).
            // Ein hart mitgesendetes `scope: "user"` würde eine büroweite Vorlage
            // beim nächsten Speichern still auf privat herabstufen und sie damit
            // für alle Kolleginnen und Kollegen verschwinden lassen.
            if (editingId === null) {
                const created = await mailTemplateService.create({
                    name: name.trim(),
                    subject,
                    bodyHtml,
                });
                setRows((prev) => [created, ...prev]);
            } else {
                const updated = await mailTemplateService.update(editingId, {
                    name: name.trim(),
                    subject,
                    bodyHtml,
                });
                setRows((prev) => prev.map((r) => (r.id === editingId ? updated : r)));
            }
            resetForm();
            setToast({ msg: "Vorlage gespeichert.", tone: "success" });
        } catch (error) {
            setToast({
                msg: reportMailError({
                    area: "mail",
                    action: "save-settings",
                    error,
                    fallbackMessage: "Die Vorlage konnte nicht gespeichert werden.",
                }),
                tone: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async (row: MailTemplate) => {
        setSavingId(row.id);
        try {
            await mailTemplateService.remove(row.id);
            setRows((prev) => prev.filter((r) => r.id !== row.id));
            if (editingId === row.id) resetForm();
            setToast({ msg: "Vorlage gelöscht.", tone: "success" });
        } catch (error) {
            setToast({
                msg: reportMailError({
                    area: "mail",
                    action: "delete-settings",
                    error,
                    fallbackMessage: "Die Vorlage konnte nicht gelöscht werden.",
                }),
                tone: "error",
            });
        } finally {
            setSavingId(null);
            setConfirmRow(null);
        }
    };

    if (loading) {
        return (
            <div className="mset">
                <div className="mset-card" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <MiniSpinner />
                    <span style={{ fontSize: 14 }}>Vorlagen werden geladen …</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mset">
                <div className="mset-card" style={{ color: "var(--mset-err)" }}>{error}</div>
            </div>
        );
    }

    return (
        <div className="mset">
            <div className="mset-card">
                <h2 className="mset-title">E-Mail-Vorlagen</h2>
                <div className="mset-hint" style={{ marginTop: -8, marginBottom: 16 }}>
                    Wiederverwendbare Betreff- und Text-Bausteine zum schnellen Vorbefüllen neuer E-Mails.
                </div>

                <div className="mset-grid2">
                    <Field label="Name">
                        <input
                            className="mset-inp"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="z. B. Terminbestätigung"
                        />
                    </Field>
                    <Field label="Betreff">
                        <input
                            className="mset-inp"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Betreff der E-Mail"
                        />
                    </Field>
                </div>

                <div className="mset-block">
                    <label className="mset-label">Inhalt</label>
                    <div className="mset-editor">
                        {/*
                         * `key` erzwingt einen Remount beim Wechsel der bearbeiteten
                         * Zeile. Ohne ihn behielte der uncontrolled Editor seinen
                         * alten Inhalt — und ein Wechsel von „Vorlage A bearbeiten"
                         * auf „Vorlage B bearbeiten" würde den Body von A in B
                         * speichern.
                         */}
                        <RichTextEditor
                            key={editingId ?? "new"}
                            ref={editorRef}
                            iconVariant="kvp"
                            initialHtml={initialBody}
                            placeholder="Sehr geehrte Damen und Herren, …"
                            ariaLabel="Vorlagen-Inhalt"
                            imageMaxBytes={SIGNATURE_IMAGE_MAX_BYTES}
                            onError={(m) => setToast({ msg: m, tone: "error" })}
                            onInput={() => { /* Dirty-Signal; Inhalt kommt aus getHtml() */ }}
                        />
                    </div>
                    <div className="mset-hint">
                        Bilder werden in die Vorlage eingebettet und beim Senden automatisch als
                        Anhang mitgeschickt (max. {Math.round(SIGNATURE_IMAGE_MAX_BYTES / 1024)} KB pro Bild).
                    </div>
                </div>

                <div className="mset-actions">
                    <button
                        type="button"
                        className="mset-btn primary"
                        disabled={submitting || !name.trim()}
                        onClick={handleSubmit}
                    >
                        {submitting ? <MiniSpinner /> : editingId === null ? "Vorlage anlegen" : "Speichern"}
                    </button>
                    {editingId !== null && (
                        <button type="button" className="mset-btn" disabled={submitting} onClick={resetForm}>
                            Abbrechen
                        </button>
                    )}
                    <button type="button" className="mset-btn" onClick={refreshPreview}>
                        Vorschau aktualisieren
                    </button>
                </div>

                {preview.trim() && (
                    <div className="mset-block">
                        <label className="mset-label">Vorschau</label>
                        <div className="mset-preview">
                            <SandboxedMailBody html={preview} blockRemote={false} minHeight={80} />
                        </div>
                    </div>
                )}
            </div>

            <div className="mset-card">
                <h2 className="mset-title">Vorhandene Vorlagen ({rows.length})</h2>
                {rows.length === 0 ? (
                    <div className="mset-empty">Noch keine Vorlagen angelegt.</div>
                ) : (
                    rows.map((row) => {
                        const busy = savingId === row.id;
                        return (
                            <div key={row.id} className={`mset-row${editingId === row.id ? " active" : ""}`}>
                                <div className="mset-row-main">
                                    <div className="mset-row-name">{row.name}</div>
                                    <div className="mset-row-meta">
                                        {row.subject || "(kein Betreff)"} ·{" "}
                                        {row.scope === "tenant" ? "Ganzes Büro" : "Nur ich"}
                                    </div>
                                </div>
                                <div className="mset-row-actions">
                                    <button
                                        type="button"
                                        className="mset-btn icon"
                                        title="Bearbeiten"
                                        disabled={busy}
                                        onClick={() => startEdit(row)}
                                    >
                                        {I.edit}
                                    </button>
                                    <button
                                        type="button"
                                        className="mset-btn icon danger"
                                        title="Löschen"
                                        disabled={busy}
                                        onClick={() => setConfirmRow(row)}
                                    >
                                        {busy ? <MiniSpinner /> : I.trash}
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <ConfirmDialog
                open={confirmRow !== null}
                title="Vorlage löschen"
                message={<>Soll die Vorlage „{confirmRow?.name}" wirklich gelöscht werden?</>}
                confirmLabel="Löschen"
                tone="danger"
                busy={savingId !== null}
                onConfirm={() => confirmRow && handleRemove(confirmRow)}
                onCancel={() => setConfirmRow(null)}
            />

            <Toast
                open={toast !== null}
                message={toast?.msg ?? ""}
                tone={toast?.tone}
                onClose={() => setToast(null)}
            />
        </div>
    );
};

export default MailTemplateSection;
